import { useRef } from 'react';
import type { Ref, ReactNode } from 'react';
import { useViewNameDialog } from './ViewNameDialog';
import type { FlowViewMachine } from './useViewStateMachine';

/**
 * ViewDirtyBox / ViewDirtyPill — the prototype's `#dirtyBox` (line 259 /
 * `renderDirty()` line 946): hidden while the working state matches the
 * active view, and otherwise a "Modified" flag plus `Reset` / `Save` /
 * `Save as new view`, in that DOM order.
 *
 * Split in two (Phase 4, plays-tab-ux-fixes): `ViewDirtyPill` is the bare
 * presentational leaf — `isDirty`/`onReset`/`disabled` plus a `children` slot
 * for whatever write actions follow Reset. `ViewDirtyBox` is unchanged from
 * the outside (same props, same DOM, same classes) — it now just supplies
 * `ViewDirtyPill` with the values it used to compute inline (`isDirty`,
 * `() => machine.reset()`, `isSaving` as `disabled`) and passes Save / Save as
 * new view / the naming dialog as children. The split exists because Plays
 * has no saved-view entity and therefore no `FlowViewMachine` to hand
 * `ViewDirtyBox` — but Phase 4 wants Plays to show the SAME "Modified [Reset]"
 * pill the standard tables do, with no Save actions at all. `PlaysSubtab.tsx`
 * renders `ViewDirtyPill` directly, with no `children`.
 *
 * Save is available ONLY on views the acting user owns (U2b, decision c) —
 * on a preset or a teammate's view the button is not rendered AT ALL rather
 * than rendered-disabled, matching the server (a PUT from a non-creator
 * 403s) with an affordance that doesn't invite a click that can't work.
 *
 * Button hierarchy (GTMOPS-2257): no dual-primary. When Save is rendered
 * (`canSave`), it is the filled-primary action — the common,
 * least-destructive path — and "Save as new view" steps down to an
 * emphasized-secondary treatment (the same blue outline/tint token set as
 * `TOOLBAR_BUTTON_ACTIVE` in `FlowTable.tsx`). When Save is ABSENT (preset or
 * teammate's view), "Save as new view" is the only write action left and
 * keeps the filled-primary treatment instead. "Save as new view" asks for
 * the name through `ViewNameDialog` (U2a) and hands it to the machine; the
 * machine never opens a naming UI of its own.
 *
 * "Modified" is its own element rather than the first words of a sentence so
 * the accessibility tree exposes a stable, assertable flag independent of any
 * surrounding copy.
 *
 * QA-2 (V4): the per-case explanation string ("— unsaved changes", "— this is
 * <creator>'s view…") was dropped — the switcher trigger already shows
 * ownership (U2b's Global/You/teammate label), so the banner repeating it was
 * redundant. No information is lost; "Modified" plus the action buttons is
 * the whole banner now.
 */

export interface ViewDirtyPillProps {
  isDirty: boolean;
  onReset: () => void;
  /** Disables the Reset button — the machine wrapper maps `isSaving` here. */
  disabled?: boolean;
  /**
   * Extra actions rendered AFTER Reset, in DOM order — the standard table's
   * Save / Save as new view and its naming dialog. Plays passes none (no
   * saved-view entity, so nothing to Save).
   */
  children?: ReactNode;
  boxRef?: Ref<HTMLDivElement>;
}

/** The bare presentational pill — see the module doc for why this exists split out from `ViewDirtyBox`. */
export function ViewDirtyPill({ isDirty, onReset, disabled = false, children, boxRef }: ViewDirtyPillProps) {
  if (!isDirty) return null;

  return (
    <div
      ref={boxRef}
      data-testid="view-dirty-box"
      className="flex items-center gap-1.5 rounded-md border border-content-warning/40 bg-bg-yellow-light px-2 py-1 text-[11.5px] text-content-primary dark:border-amber-700/50 dark:text-nightshade-100"
    >
      <span className="font-semibold">Modified</span>
      <button
        type="button"
        disabled={disabled}
        onClick={onReset}
        className="rounded border border-secondary bg-white px-1.5 py-0.5 text-content-secondary hover:bg-bg-secondary disabled:opacity-50 dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-400 dark:hover:bg-nightshade-700"
      >
        Reset
      </button>
      {children}
    </div>
  );
}

export interface ViewDirtyBoxProps {
  machine: FlowViewMachine;
}

export function ViewDirtyBox({ machine }: ViewDirtyBoxProps) {
  const { isDirty, canSave, isSaving } = machine;
  const boxRef = useRef<HTMLDivElement>(null);
  const { requestName, dialog } = useViewNameDialog(boxRef);

  const handleSaveAsNew = async () => {
    const name = await requestName({
      title: 'Name this view',
      initialValue: `${machine.activeView.name} (my copy)`,
      confirmLabel: 'Save view',
    });
    if (name === null) return;
    await machine.saveAsNew(name);
  };

  return (
    <ViewDirtyPill isDirty={isDirty} onReset={() => machine.reset()} disabled={isSaving} boxRef={boxRef}>
      {/* Not rendered at all on a preset or a teammate's view — see the
          module doc: a disabled-with-tooltip Save invited a click that could
          never work, where the server itself just 403s a non-creator PUT. */}
      {canSave && (
        <button
          type="button"
          disabled={isSaving}
          onClick={() => { void machine.save(); }}
          className="rounded border border-content-action bg-content-action px-1.5 py-0.5 text-white hover:bg-content-action-hover disabled:opacity-50"
        >
          Save
        </button>
      )}
      {/* No dual-primary: when Save is rendered (canSave), it takes the
          filled-primary treatment as the common/least-destructive action and
          "Save as new view" steps down to an emphasized-secondary (blue
          outline/tint, `TOOLBAR_BUTTON_ACTIVE` in `FlowTable.tsx`). When Save
          is ABSENT (preset or teammate's view — see the module doc), "Save as
          new view" is the only write action left and keeps the filled-primary
          treatment instead. */}
      <button
        type="button"
        disabled={isSaving}
        onClick={() => { void handleSaveAsNew(); }}
        className={
          canSave
            ? 'rounded border border-content-action bg-bg-blue-light px-1.5 py-0.5 font-medium text-content-action hover:bg-bg-blue disabled:opacity-50 dark:border-blueberry-700 dark:text-blueberry-400'
            : 'rounded border border-content-action bg-content-action px-1.5 py-0.5 text-white hover:bg-content-action-hover disabled:opacity-50 dark:border-blueberry-500 dark:bg-blueberry-500'
        }
      >
        Save as new view
      </button>
      {dialog}
    </ViewDirtyPill>
  );
}
